import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const COLORS = ["#ff2bfd", "#ff2fa0", "#a838ff", "#00e5ff", "#ffc93c", "#ffffff"];

function randomHex() {
  return COLORS[Math.floor(Math.random() * COLORS.length)];
}

/** STREAK FIREWORKS — canvas particle bursts + the player's name on the big screen. */
export function Fireworks({ events, durationSec = 6, testid = "tv-fireworks" }) {
  const canvasRef = useRef(null);
  const [active, setActive] = useState(null);
  const seenRef = useRef("");

  useEffect(() => {
    const list = events || [];
    const latest = list[list.length - 1];
    if (!latest) return;
    const key = `${latest.player_id}:${latest.streak}`;
    if (key === seenRef.current) return;
    seenRef.current = key;
    setActive(latest);
    const t = setTimeout(() => setActive(null), durationSec * 1000);
    return () => clearTimeout(t);
  }, [events, durationSec]);

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    let raf;
    let running = true;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const resize = () => {
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const parts = [];
    const burst = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const x = w * (0.15 + Math.random() * 0.7);
      const y = h * (0.12 + Math.random() * 0.4);
      const color = randomHex();
      const n = 42 + Math.floor(Math.random() * 30);
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / n + Math.random() * 0.3;
        const v = 90 + Math.random() * 240;
        parts.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 1, color, size: 1.6 + Math.random() * 2.4 });
      }
    };
    burst();
    const spawner = setInterval(burst, 620);
    let last = performance.now();
    const frame = (now) => {
      if (!running) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
      ctx.globalCompositeOperation = "lighter";
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i];
        p.life -= dt * 0.62;
        if (p.life <= 0) { parts.splice(i, 1); continue; }
        p.vy += 190 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      running = false;
      clearInterval(spawner);
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="pointer-events-none fixed inset-0 z-30" data-testid={testid}>
          <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
          <motion.div className="absolute left-1/2 top-[16%] -translate-x-1/2 text-center"
            initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 14 }}>
            <div className="rounded-full border border-amber/40 bg-navy-900/80 px-6 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.4em] text-amber-soft backdrop-blur">
              {active.name}
            </div>
            <div className="grad-text text-glow-magenta mt-3 font-display text-[9vh] font-extrabold uppercase leading-none"
              style={{ textShadow: "0 0 40px rgba(255,43,253,0.55)" }}>
              {active.streak} in a row!
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
